import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { FileAttachment } from 'api/attachments';
import { ReactComponent as CurrentVersionIcon } from 'assets/svgs/current-version.svg';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { confirm } from 'components/atoms/modal';
import { RelativeTime } from 'components/atoms/relative-time';
import { RoutePaths } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import {
  useAttachmentVersionDeleteMutation,
  useMarkAsCurrentVersion,
} from 'queries/attachments';
import { extname, isImageUrlFromExtension } from 'utils/files';
import {
  AnnotationVersionListWrapper,
  AnnotationVersionWrapper,
  CompareIcon,
  Image,
  MoreActionsDropdownWrapper,
  TrailBar,
  VersionDetail,
  VersionImage,
  VersionName,
  VersionTime,
} from './annotation-version-style-components';
import styles from './file-thumbnail.module.css';

interface AnnotationVersionListProps {
  latestVersion: FileAttachment;
  versionFileList: FileAttachment[];
  selectedVersion: FileAttachment;
  setVersionFileList: (attachment: FileAttachment[]) => void;
  setSelectedVersion: (attachment: FileAttachment) => void;
  setLatestVersion: (attachment: FileAttachment) => void;
  setLeftAttachment: (attachment: FileAttachment) => void;
  setRightAttachment: (attachment: FileAttachment) => void;
}

export const AnnotationVersionList: FC<AnnotationVersionListProps> = ({
  latestVersion,
  versionFileList,
  selectedVersion,
  setSelectedVersion,
  setLatestVersion,
  setLeftAttachment,
  setRightAttachment,
}) => {
  const fileListLength = versionFileList.length;
  const history = useHistory();
  const translate = useTranslate();

  const { mutateAsync: deleteAttachmentVersion } =
    useAttachmentVersionDeleteMutation();

  const { mutateAsync: markAsCurrentVersion } = useMarkAsCurrentVersion();

  const deleteConfirmation = (attachmentId) => {
    confirm({
      okText: translate('delete'),
      okType: 'danger',
      cancelText: translate('cancel'),
      onCancel: () => {},
      title: translate('image_preview.version.delete.title'),
      content:
        versionFileList.filter((version) => version.isDeleted === 'false')
          .length > 1 ? (
          translate('image_preview.version.delete.content', {
            versionNo: 'Version',
          })
        ) : (
          <>
            {translate('image_preview.version.delete.content', {
              versionNo: 'Version',
            })}
            <div>{translate('image_preview.version.delete.attachment')}</div>
          </>
        ),
      onOk: () => {
        deleteAttachmentVersion(attachmentId).then(() => {
          if (
            versionFileList.filter(
              (attachment) => attachment.isDeleted === 'false',
            ).length === 1
          ) {
            history.goBack();
          }

          if (latestVersion.attachmentId === attachmentId) {
            const secondLatestVersion = versionFileList.filter(
              (attachment) => attachment.isDeleted === 'false',
            )[1];

            setSelectedVersion(secondLatestVersion);
            setLatestVersion(secondLatestVersion);
            history.replace(
              RoutePaths.attachmentWorkspace.replace(
                ':attachmentId',
                secondLatestVersion.attachmentId.toString(),
              ),
            );
            // }
          } else {
            setSelectedVersion(latestVersion);
          }
        });
      },
    });
  };

  const updateSelectedVersion = useCallback(
    (file: FileAttachment) => {
      setSelectedVersion(file);
      history.replace(
        RoutePaths.attachmentWorkspace.replace(
          ':attachmentId',
          file.attachmentId.toString(),
        ),
      );
    },
    [history, setSelectedVersion],
  );

  const onComapreWithVersionClick = useCallback(
    (selectedVersionValue, file) => {
      setLeftAttachment(selectedVersionValue);
      setRightAttachment(file);
      history.replace(
        RoutePaths.attachmentWorkspaceSideBySide
          .replace(
            ':attachmentId',
            selectedVersionValue.attachmentId.toString(),
          )
          .replace(':compareAttachmentId', file.attachmentId.toString()),
      );
    },
    [history, setLeftAttachment, setRightAttachment],
  );

  const onMakeCurrentVersionClick = useCallback(
    (file) => {
      markAsCurrentVersion({
        attachmentId: file.attachmentId,
        versionMappingId: Number(file.versionMappingId),
      }).then(() => {
        setSelectedVersion(file);
        setLatestVersion(file);
      });
    },
    [markAsCurrentVersion, setLatestVersion, setSelectedVersion],
  );

  const MoreActionsMenu = (file: FileAttachment) => {
    const { attachmentId: attachmentIdValue } = file;
    const isSelectedVersion =
      selectedVersion.attachmentId !== attachmentIdValue;

    return (
      <Menu
        onClick={(e) => e.domEvent.stopPropagation()}
        className="more-actions-menu"
      >
        {isSelectedVersion ||
        attachmentIdValue !== latestVersion.attachmentId ? (
          <Menu.Header>
            <FormattedMessage id="more_actions" />
          </Menu.Header>
        ) : null}

        {isSelectedVersion && (
          <Menu.Item
            icon={<CompareIcon />}
            onClick={() => onComapreWithVersionClick(selectedVersion, file)}
          >
            <FormattedMessage id="image_preview.compare.with.version" />
          </Menu.Item>
        )}

        {attachmentIdValue !== latestVersion.attachmentId && (
          <Menu.Item
            icon={<CurrentVersionIcon />}
            onClick={() => onMakeCurrentVersionClick(file)}
          >
            <FormattedMessage id="image_preview.set.current.version" />
          </Menu.Item>
        )}

        <>
          {isSelectedVersion ||
          attachmentIdValue !== latestVersion.attachmentId ? (
            <Menu.Divider />
          ) : null}
          <Menu.ItemDelete
            data-cy="attachement-version-more-actions__delete"
            key="delete"
            onClick={() => deleteConfirmation(file.attachmentId)}
          />
        </>
      </Menu>
    );
  };

  const onVersionToggle = useCallback(
    (file) => {
      if (file.isDeleted === 'false') {
        updateSelectedVersion(file);
        setLeftAttachment(file);
      }
    },
    [setLeftAttachment, updateSelectedVersion],
  );

  return (
    <AnnotationVersionListWrapper>
      {versionFileList.map((file, index) => {
        const fileExtension = extname(file.location || file.name);
        const isImage = isImageUrlFromExtension(file.location || file.name);

        return (
          <>
            <AnnotationVersionWrapper
              onClick={() => onVersionToggle(file)}
              isDeleted={file.isDeleted}
            >
              {file.isDeleted === 'false' && (
                <VersionImage
                  isSelectedVersion={
                    selectedVersion.attachmentId === file.attachmentId
                  }
                >
                  {isImage ? (
                    <Image src={file.location} alt="" />
                  ) : (
                    <div
                      className={classNames(
                        styles.item,
                        styles.item__other,
                        styles.item__pdf,
                      )}
                      role="button"
                      tabIndex={0}
                    >
                      <h6 className="h6">{fileExtension}</h6>
                    </div>
                  )}

                  <MoreActionsDropdownWrapper
                    overlay={MoreActionsMenu(file)}
                    trigger={['hover']}
                    placement="bottomRight"
                  >
                    <Tooltip
                      title={<FormattedMessage id="task.more_actions" />}
                    >
                      <Button
                        type="text"
                        icon={<OverflowMenuVertical16 />}
                        size="small"
                        data-cy="task-more-actions"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  </MoreActionsDropdownWrapper>
                </VersionImage>
              )}

              <VersionDetail>
                <VersionName
                  isSelectedVersion={
                    selectedVersion.attachmentId === file.attachmentId
                  }
                >
                  {file.isDeleted === 'true' ? (
                    <FormattedMessage
                      id="image_preview.version.no.is.deleted"
                      values={{
                        number: fileListLength - index,
                      }}
                    />
                  ) : file.latestVersion ? (
                    <FormattedMessage id="image_preview.current.version" />
                  ) : (
                    <FormattedMessage
                      id="image_preview.version.no"
                      values={{
                        number: fileListLength - index,
                      }}
                    />
                  )}
                </VersionName>
                <VersionTime
                  isSelectedVersion={
                    selectedVersion.attachmentId === file.attachmentId
                  }
                >
                  <HumanisedDate timestamp={moment(file?.createdAt)} />
                  <span>&nbsp; &#8259; &nbsp;</span>
                  <RelativeTime time={moment(file?.createdAt)} />
                </VersionTime>
              </VersionDetail>
            </AnnotationVersionWrapper>

            {fileListLength - index > 1 && <TrailBar />}
          </>
        );
      })}
    </AnnotationVersionListWrapper>
  );
};
